import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  IMyKnownError,
  REQUEST_LIFE_CICLE,
  IPaginatedListResponse,
} from 'interfaces/requests';

import { get, MICROSERVICES } from 'api';
import endpoints from './endpoints';
import {
  IEPSDetailListState,
  IEPSRemunerationParams,
  IInvoiceDetailListState,
  IInvoiceRemunerationParams,
  IRemunerationEPSDetailResponse,
  IRemunerationInvoiceDetailResponse,
  IRemunerationServiceCategoryDetailResponse,
  IRemunerationSummaryByServiceCategoryResponse,
  IRemunerationSummaryListResponse,
  IRemunerationSummaryResponse,
  IServiceCategoryDetailListState,
  IServiceCategoryRemunerationParams,
  ISummaryFilters,
  ISummaryFiltersRequest,
  ISummaryListState,
} from './interfaces';
import { openAlert } from 'modules/alertHandler';
import { store } from 'store';
import { parsedFiltersRequest, parsedFiltersState } from './helpers';

export const loadSummaryListAction = createAsyncThunk<
  ISummaryListState,
  { filters: ISummaryFilters; onFinish: () => void },
  { rejectValue: IMyKnownError }
>(
  'summaryList/fetch',
  async ({ filters, onFinish }, { rejectWithValue, dispatch }) => {
    try {
      const params = parsedFiltersRequest(filters);

      const result = await get<
        IPaginatedListResponse<IRemunerationSummaryResponse>
      >(MICROSERVICES.REMUNERATIONS, {
        url: endpoints.summaryEPS(),
        params,
      });

      const { epsByKey } = store.getState().epsList;
      const summaryList = result.data.values.map((item) => ({
        ...item,
        id: item._id,
        epsName:
          epsByKey[item.epsTotal]?.fantasyName ||
          epsByKey[item.epsTotal]?.companyName ||
          'EPS Sem Nome',
      }));

      if (summaryList.length === 0) {
        dispatch(
          openAlert({
            title: 'Sem resultados',
            content: 'A pesquisa realizada não retornou nenhum resultado',
          }),
        );
        dispatch(clearRemunerationSummary());
      }

      const paramsWithoutEPS: ISummaryFiltersRequest = {
        paymentDateEnd: params.paymentDateEnd,
        paymentDateStart: params.paymentDateStart,
      };

      const totalResult = await dispatch(
        loadSummaryTotalAction(paramsWithoutEPS),
      );

      onFinish && onFinish();

      return {
        count: result.data.count,
        list: summaryList,
        serviceCategoriesTotal: totalResult.payload,
        filters: parsedFiltersState(filters),
      } as ISummaryListState;
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content:
            'Ocorreu algum erro ao tentar visualizar o resumo das remunerações.',
        }),
      );
      onFinish && onFinish();
      console.error('error at load remuneration summary list', error);
      return rejectWithValue(error.message);
    }
  },
);

export const loadSummaryTotalAction = createAsyncThunk<
  IRemunerationSummaryListResponse,
  ISummaryFiltersRequest,
  { rejectValue: IMyKnownError }
>('summaryTotal/fetch', async (params, { rejectWithValue, dispatch }) => {
  try {
    const { filters } = store.getState().remunerationSummary;
    const paymentDateEnd = params.paymentDateEnd || filters.endDate;
    const paymentDateStart = params.paymentDateStart || filters.startDate;

    if (!paymentDateEnd || !paymentDateEnd) {
      return initialState.serviceCategoriesTotal;
    }

    const filtersWithoutEPS: ISummaryFiltersRequest = {
      paymentDateEnd: String(paymentDateEnd),
      paymentDateStart: String(paymentDateStart),
    };
    const orderParams =
      params.orderBy && params.orderDirection
        ? {
            orderBy: params.orderBy,
            orderDirection: params.orderDirection,
          }
        : {};
    const resultTotalByServiceCategories = await get<
      IPaginatedListResponse<IRemunerationSummaryByServiceCategoryResponse>
    >(MICROSERVICES.REMUNERATIONS, {
      url: endpoints.summaryTotal(),
      params: {
        ...orderParams,
        ...filtersWithoutEPS,
      },
    });

    const totalByServiceCategoryList =
      resultTotalByServiceCategories.data.values;

    const total =
      totalByServiceCategoryList.length === 0
        ? initialState.serviceCategoriesTotal.total
        : totalByServiceCategoryList.reduce(
            (summaryTotal, current) => {
              return {
                recoveredValue:
                  summaryTotal.recoveredValue + current.recoveredValue,
                remunerationValue:
                  summaryTotal.remunerationValue + current.remunerationValue,
              };
            },
            {
              recoveredValue: 0,
              remunerationValue: 0,
            },
          );

    return {
      total,
      list: totalByServiceCategoryList,
    };
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content:
          'Ocorreu algum erro ao tentar visualizar os totais por tipo de atuação.',
      }),
    );
    console.error('error at load remuneration summary total list', error);
    return rejectWithValue(error.message);
  }
});

export const loadRemunerationEPSDetailsAction = createAsyncThunk<
  IEPSDetailListState,
  IEPSRemunerationParams,
  { rejectValue: IMyKnownError }
>(
  'remunerationEPSDetails/fetch',
  async (
    { epsId, paymentDateStart, paymentDateEnd },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const result = await get<
        IPaginatedListResponse<IRemunerationEPSDetailResponse>
      >(MICROSERVICES.REMUNERATIONS, {
        url: endpoints.summaryEPSDetails(epsId),
        params: {
          paymentDateStart,
          paymentDateEnd,
          limit: 9999999,
          offset: 0,
        },
      });

      const detailList = result.data.values.map((item) => ({
        ...item,
        id: item._id,
      }));
      return {
        count: result.data.count,
        list: detailList,
      };
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content:
            'Ocorreu algum erro ao tentar visualizar o detalhe pela EPS.',
        }),
      );
      console.error(
        `error at load remuneration summary detail by eps id (${epsId})`,
        error,
      );
      return rejectWithValue(error.message);
    }
  },
);

export const loadRemunerationServiceCategoryDetailsAction = createAsyncThunk<
  IServiceCategoryDetailListState,
  IServiceCategoryRemunerationParams,
  { rejectValue: IMyKnownError }
>(
  'remunerationServiceCategoryDetails/fetch',
  async (
    { serviceCategoryId, paymentDateStart, paymentDateEnd },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const params = parsedFiltersRequest({
        startDate: paymentDateStart,
        endDate: paymentDateEnd,
      });
      const result = await get<
        IPaginatedListResponse<IRemunerationServiceCategoryDetailResponse>
      >(MICROSERVICES.REMUNERATIONS, {
        url: endpoints.summaryServiceCategoryDetails(serviceCategoryId),
        params: {
          ...params,
          limit: 9999999,
          offset: 0,
        },
      });

      const detailServiceCategoryList = result.data.values.map((item) => ({
        ...item,
        id: item._id,
      }));
      return {
        count: result.data.count,
        list: detailServiceCategoryList,
      };
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content:
            'Ocorreu algum erro ao tentar visualizar o detalhe pelo Tipo de Atuação.',
        }),
      );
      console.error(
        `error at load remuneration summary detail by service category id (${serviceCategoryId})`,
        error,
      );
      return rejectWithValue(error.message);
    }
  },
);

export const loadRemunerationInvoiceDetailsAction = createAsyncThunk<
  IInvoiceDetailListState,
  IInvoiceRemunerationParams,
  { rejectValue: IMyKnownError }
>(
  'remunerationInvoiceDetails/fetch',
  async (
    { serviceCategoryId, epsId, paymentDateStart, paymentDateEnd },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const params = parsedFiltersRequest({
        startDate: paymentDateStart,
        endDate: paymentDateEnd,
        eps: epsId,
        serviceCategory: serviceCategoryId,
      });
      const result = await get<
        IPaginatedListResponse<IRemunerationInvoiceDetailResponse>
      >(MICROSERVICES.REMUNERATIONS, {
        url: endpoints.invoiceDetails(),
        params: {
          ...params,
          limit: 9999999,
          offset: 0,
        },
      });

      const detailInvoiceList = result.data.values.map((item) => ({
        ...item,
        id: item._id,
      }));
      return {
        count: result.data.count,
        list: detailInvoiceList,
      };
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content:
            'Ocorreu algum erro ao tentar visualizar o detalhe pela fatura.',
        }),
      );
      console.error(
        `error at load remuneration summary detail by invoice id (${serviceCategoryId})`,
        error,
      );
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  list: [],
  serviceCategoriesTotal: {
    total: {},
    list: [],
  },
  count: 0,
  EPSDetails: {},
  serviceCategoryDetails: {},
  invoiceDetails: {},
  filters: {},
} as ISummaryListState;

const exemptionListSlice = createSlice({
  name: 'summaryList',
  initialState,
  reducers: {
    clearRemunerationSummary: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(loadSummaryListAction.fulfilled, (state, action) => {
      state.count = action.payload.count;
      state.list = action.payload.list;
      state.serviceCategoriesTotal = action.payload.serviceCategoriesTotal;
      state.filters = action.payload.filters;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(loadSummaryListAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(loadSummaryListAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });

    builder.addCase(loadSummaryTotalAction.fulfilled, (state, action) => {
      state.serviceCategoriesTotal = action.payload;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(loadSummaryTotalAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(loadSummaryTotalAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });

    builder.addCase(
      loadRemunerationEPSDetailsAction.fulfilled,
      (state, action) => {
        state.EPSDetails = action.payload;
        state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      },
    );
    builder.addCase(loadRemunerationEPSDetailsAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(loadRemunerationEPSDetailsAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });

    builder.addCase(
      loadRemunerationServiceCategoryDetailsAction.fulfilled,
      (state, action) => {
        state.serviceCategoryDetails = {
          ...state.serviceCategoryDetails,
          ...action.payload,
        };
        state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      },
    );
    builder.addCase(
      loadRemunerationServiceCategoryDetailsAction.pending,
      (state) => {
        state.loading = REQUEST_LIFE_CICLE.PENDING;
      },
    );
    builder.addCase(
      loadRemunerationServiceCategoryDetailsAction.rejected,
      (state) => {
        state.loading = REQUEST_LIFE_CICLE.FAILED;
      },
    );

    builder.addCase(
      loadRemunerationInvoiceDetailsAction.fulfilled,
      (state, action) => {
        state.invoiceDetails = action.payload;
        state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      },
    );
    builder.addCase(loadRemunerationInvoiceDetailsAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(loadRemunerationInvoiceDetailsAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });
  },
});

const { reducer, actions } = exemptionListSlice;
export const { clearRemunerationSummary } = actions;
export default reducer;
